import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crown, History, Share2 } from "lucide-react";
import api from "@/lib/api";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const monthLabel = (mstr) => {
  const [y, m] = (mstr || "").split("-");
  return m ? `${MONTHS[parseInt(m, 10) - 1]} ${y}` : mstr;
};

export default function PilotMonthAlbo() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/pilot-of-the-month/history").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  if (!rows || rows.length === 0) return null;

  const shareAlbo = async () => {
    const W = 1080, rowH = 118, headH = 320, footH = 60;
    const H = headH + rows.length * rowH + footH;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#09090b"; ctx.fillRect(0, 0, W, H);
    ctx.save();
    ctx.globalAlpha = 0.06; ctx.fillStyle = "#facc15";
    for (let i = -2; i < 14; i++) ctx.fillRect(i * 90, 0, 30, H);
    ctx.restore();
    ctx.fillStyle = "#facc15"; ctx.fillRect(0, 0, W, 12);
    ctx.fillStyle = "#facc15"; ctx.fillRect(0, H - 12, W, 12);

    const logo = new Image();
    logo.src = "/team-tuono-logo.png";
    await new Promise((res) => { logo.onload = res; logo.onerror = res; });
    if (logo.complete && logo.naturalWidth) { try { ctx.drawImage(logo, W - 184, 52, 128, 128); } catch (e) { /* skip */ } }

    ctx.textAlign = "left";
    ctx.fillStyle = "#facc15"; ctx.font = "bold 30px Arial"; ctx.fillText("TEAM TUONO", 64, 108);
    ctx.fillStyle = "#fafafa"; ctx.font = "900 74px Arial"; ctx.fillText("ALBO PILOTI DEL MESE", 64, 196);
    ctx.fillStyle = "#a1a1aa"; ctx.font = "30px Arial"; ctx.fillText("Hall of Fame · vincitori mese per mese", 64, 246);

    let y = headH;
    rows.forEach((r, i) => {
      ctx.fillStyle = i % 2 ? "#0e0e11" : "#16161a";
      ctx.fillRect(48, y - 66, W - 96, rowH - 16);
      // crown
      ctx.textAlign = "left"; ctx.font = "56px Arial"; ctx.fillText("\u{1F451}", 74, y - 8);
      ctx.fillStyle = "#facc15"; ctx.font = "bold 24px Arial";
      ctx.fillText(monthLabel(r.month).toUpperCase(), 168, y - 34);
      ctx.fillStyle = "#fafafa"; ctx.font = "900 42px Arial";
      ctx.fillText((r.pilot.name || "").slice(0, 22), 168, y + 8);
      ctx.fillStyle = "#06b6d4"; ctx.font = "900 40px Arial"; ctx.textAlign = "right";
      ctx.fillText(`${r.pilot.points} pt`, W - 74, y - 12);
      ctx.fillStyle = "#71717a"; ctx.font = "22px Arial";
      ctx.fillText(`${r.pilot.wins} vittorie`, W - 74, y + 20);
      y += rowH;
    });
    ctx.textAlign = "left";

    const blob = await new Promise((res) => c.toBlob(res, "image/png"));
    const file = new File([blob], "albo-piloti-del-mese.png", { type: "image/png" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Albo Piloti del Mese", text: "Albo Piloti del Mese — Team Tuono" });
        return;
      }
    } catch (e) { /* fallback to download */ }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "albo-piloti-del-mese.png"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Locandina Albo generata");
  };

  return (
    <div data-testid="pilot-month-albo" className="mb-10">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-yellow-400" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-[0.15em] text-zinc-200">Albo Piloti del Mese</h2>
        </div>
        <button onClick={shareAlbo} data-testid="share-albo-btn"
          className="inline-flex items-center gap-2 border border-yellow-400/40 text-yellow-400 hover:bg-yellow-400/10 text-xs font-bold tracking-[0.15em] uppercase px-4 py-2.5 transition-colors duration-200">
          <Share2 className="h-4 w-4" /> Condividi Albo
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="albo-grid">
        {rows.map((row, i) => (
          <motion.div key={row.month} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
            <Link to={`/piloti/${encodeURIComponent(row.pilot.name)}`} data-testid={`albo-row-${row.month}`}
              className="flex items-center gap-3 border border-zinc-800 bg-gradient-to-r from-yellow-400/5 to-zinc-900 p-4 hover:border-yellow-400/50 transition-colors duration-200">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-yellow-400/15 border border-yellow-400/40">
                <Crown className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-yellow-300/70">{monthLabel(row.month)}</div>
                <div className="font-heading text-base font-bold text-zinc-50 truncate">{row.pilot.name}</div>
                <div className="text-xs text-zinc-500">{row.pilot.points} punti · {row.pilot.wins} vittorie</div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
